import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { customerState, useTranslate } from '@repo/unicity'
import { ActionButton, ErrorMessages, renderFieldRows, type CountryItem } from '@/core/components'
import { useCurrentLanguage } from '@/core/utils'
import { useOmanCheckout } from '../../../hooks/useOmanCheckout'
import { useRegionsByCountry } from '../../../hooks/useRegionsByCountry'
import {
    omanCountryListAtom,
    isLoadingOmanGeoAtom,
    isLoadingOmanFormatAtom,
    omanSelectedShippingMethodAtom,
    omanFormatDataAtom,
    omanMemberShippingFormAtom,
    omanDeliveryFreightSyncedUuidAtom,
    omanCartSyncingAtom
} from '../../../stores/oman.store'
import { memberShippingFields, pickupShippingFields, omanConfig } from '../../../oman.config'
import OmanShippingMethod from './OmanShippingMethod'
import type { TOmanShippingContact, TOmanShippingForm, TOmanShippingMethodId } from '../../../types/oman.types'

export const SectionCard = styled(Box)`
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 30px;
    background: #ffffff;
`

const DELIVERY_FREIGHT_SYNC_OPTIONS = { updateSelection: false } as const
const PICKUP_SUBMIT_OPTIONS = { showSkeleton: false, refetchFormat: false } as const

const MemberShippingStep = () => {
    const { translate } = useTranslate()
    const currentLanguage = useCurrentLanguage()
    const { loading, errors, submitMemberShipping, syncShippingMethod, uuid } = useOmanCheckout()
    const countryList = useAtomValue(omanCountryListAtom)
    const geoLoading = useAtomValue(isLoadingOmanGeoAtom)
    const isLoadingFormat = useAtomValue(isLoadingOmanFormatAtom)
    const isCartSyncing = useAtomValue(omanCartSyncingAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const formatData = useAtomValue(omanFormatDataAtom)
    const navigate = useNavigate()
    const hydratedFormatUuidRef = useRef<string | null>(null)
    const hydratedCustomerRef = useRef(false)
    const formDisabled = loading || isLoadingFormat || isCartSyncing

    const customer = useAtomValue(customerState)

    const [form, setForm] = useAtom(omanMemberShippingFormAtom)
    const [syncedDeliveryFreightUuid, setSyncedDeliveryFreightUuid] = useAtom(omanDeliveryFreightSyncedUuidAtom)
    const { regions, regionsLoading } = useRegionsByCountry(form.country)

    useLayoutEffect(() => {
        if (!uuid) return
        if (syncedDeliveryFreightUuid === uuid) return
        setSyncedDeliveryFreightUuid(uuid)
        void syncShippingMethod('delivery', true, undefined, DELIVERY_FREIGHT_SYNC_OPTIONS)
    }, [setSyncedDeliveryFreightUuid, syncShippingMethod, syncedDeliveryFreightUuid, uuid])

    // Prefill priority:
    //   1. `formatData` (server-saved checkout state) — wins for fields it
    //      provides (fullname / email / phone). Keyed by `uuid` so a new
    //      checkout session re-seeds the form.
    //   2. `customer` (logged-in member profile) — fills the remaining empty
    //      fields (address1/2/city) and acts as fallback for
    //      fullname/email/phone when `formatData` has none. Gated by
    //      `hydratedCustomerRef` so it runs only once per mount.
    //
    // Both effects write with `prev.X || source.X` so user edits are never
    // stomped when data arrives late (customer atom often hydrates after the
    // first render).

    useEffect(() => {
        if (!formatData?.uuid) return
        if (hydratedFormatUuidRef.current === formatData.uuid) return

        const hasFormatDefaults = Boolean(formatData.fullname || formatData.email || formatData.phone)

        if (!hasFormatDefaults) return

        setForm(prev => ({
            ...prev,
            fullname: prev.fullname || formatData.fullname || '',
            email: prev.email || formatData.email || '',
            phone: prev.phone || formatData.phone || ''
        }))
        hydratedFormatUuidRef.current = formatData.uuid
    }, [formatData, setForm])

    useEffect(() => {
        if (hydratedCustomerRef.current) return

        const humanName = customer?.humanName?.fullName
        const addr = customer?.mainAddress
        const hasCustomerDefaults = Boolean(humanName || customer?.email || customer?.mobilePhone || addr?.address1)
        if (!hasCustomerDefaults) return

        setForm(prev => ({
            ...prev,
            fullname: prev.fullname || humanName || '',
            email: prev.email || customer?.email || '',
            phone: prev.phone || customer?.mobilePhone || '',
            address1: prev.address1 || addr?.address1 || '',
            address2: prev.address2 || addr?.address2 || '',
            city: prev.city || addr?.city || ''
        }))
        hydratedCustomerRef.current = true
    }, [customer, setForm])

    // Per-key handler cache. Without this, calling `set('email')` returns a
    // fresh closure every render, which busts `FieldCell`'s memo and causes
    // every field to re-render on every keystroke. The ref keeps the same
    // function reference across renders for the same key.
    const handlersRef = useRef<Record<string, (e: React.ChangeEvent<HTMLInputElement>) => void>>({})
    const set = useCallback(
        (key: string) => {
            if (!handlersRef.current[key]) {
                handlersRef.current[key] = e => setForm(prev => ({ ...prev, [key]: e.target.value }))
            }
            return handlersRef.current[key]
        },
        [setForm]
    )

    const mappedRegions = useMemo(() => regions.map(r => ({ name: r.name, id: r.value })), [regions])

    const getCountryDisplayName = useCallback(
        (item: CountryItem) => (currentLanguage === 'AR' && item.name_native ? item.name_native : item.name_english),
        [currentLanguage]
    )
    const selectedCountry = useMemo(() => countryList.find(item => item.country_code_2 === form.country), [countryList, form.country])
    const countryDisplayName = selectedCountry ? getCountryDisplayName(selectedCountry) : form.country
    const shippingFields = selectedShippingMethod === 'pickup' ? pickupShippingFields : memberShippingFields

    const fieldCtx = useMemo(
        () => ({
            form: form as unknown as Record<string, unknown>,
            setForm,
            set,
            formDisabled,
            dialCode: omanConfig.dialCode,
            countryList,
            geoLoading,
            regions: mappedRegions,
            regionsLoading,
            getCountryDisplayName,
            countryDisplayName,
            countryFlagUrl: selectedCountry?.flags_url,
            testIdPrefix: 'member-shipping-step'
        }),
        [
            form,
            setForm,
            set,
            formDisabled,
            countryList,
            geoLoading,
            mappedRegions,
            regionsLoading,
            getCountryDisplayName,
            countryDisplayName,
            selectedCountry?.flags_url
        ]
    )

    const getPickupContact = useCallback(
        (values: TOmanShippingForm): TOmanShippingContact => ({
            fullname: values.fullname,
            email: values.email,
            phone: values.phone
        }),
        []
    )

    const handleMethodChange = useCallback((method: TOmanShippingMethodId) => syncShippingMethod(method, true), [syncShippingMethod])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (selectedShippingMethod === 'pickup') {
            if (uuid) {
                const ok = await syncShippingMethod('pickup', false, getPickupContact(form), PICKUP_SUBMIT_OPTIONS)
                if (ok) navigate(`/checkout/payment?uuid=${uuid}`)
            }
            return
        }
        submitMemberShipping(form)
    }

    return (
        <Stack component="form" onSubmit={handleSubmit} spacing={3} data-testid="member-shipping-step-stack-handle-submit">
            <SectionCard data-testid="member-shipping-step-section-card-1">
                <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#003764', mb: 2 }} data-testid="member-shipping-step-typography-shipping-method">
                    {translate('shipping_method')}
                </Typography>
                <OmanShippingMethod disabled={formDisabled} onMethodChange={handleMethodChange} data-testid="member-shipping-step-oman-shipping-method" />
            </SectionCard>
            <SectionCard data-testid="member-shipping-step-section-card-2">
                <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#003764', mb: 3 }} data-testid="member-shipping-step-typography-ship-to">
                    {translate('ship_to')}:
                </Typography>

                <Stack spacing={2.5} data-testid="member-shipping-step-stack">
                    {renderFieldRows(shippingFields, fieldCtx)}
                </Stack>

                <ErrorMessages list={errors} sx={{ mt: 2 }} data-testid="member-shipping-step-error-messages" />

                <Box sx={{ mt: '30px' }} data-testid="member-shipping-step-box-action-button-submit">
                    <ActionButton
                        text={formDisabled ? '' : translate('continue_to_payment')}
                        type="submit"
                        disabled={formDisabled}
                        startIcon={
                            formDisabled ? <CircularProgress size={22} color="inherit" data-testid="member-shipping-step-circular-progress" /> : undefined
                        }
                        data-testid="member-shipping-step-action-button-submit"
                    />
                </Box>
            </SectionCard>
        </Stack>
    )
}

export default MemberShippingStep
